import { Button } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import {
	DataGrid,
	GridColDef,
	GridOverlay,
	GridRowParams,
	GridRowsProp,
} from '@mui/x-data-grid';
import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
	Link as RouterLink,
	NavigateFunction,
	useParams,
} from 'react-router-dom';
import AuxDataApi from '../../api/AuxDataApi';
import { handleUnauthorizedResponse } from '../../api/http';
import { AuthContext, AuthData } from '../../context/auth';
import { ConfigContext, ConfigData } from '../../context/config';
import { ErrorContext, ErrorData } from '../../context/ErrorProvider';
import { pageTitleSize } from '../../data/globalStylingVariables';
import {
	AuxDataKeyResponse,
	AuxDataResponse,
	AuxDataUITypes,
} from '../../types/auxData';
import { StyledTitleContainer } from '../common';
import AuxDataCreateModalTrigger from './AuxDataCreateModalTrigger';
import AuxDataDeleteModalTrigger from './AuxDataDeleteModalTrigger';
import AuxDataEditModalTrigger from './AuxDataEditModalTrigger';

interface Props {
	authData: AuthData;
	configData: ConfigData;
	errorData: ErrorData;
	entityType: string;
	entityLocator?: string;
	backUrl?: string;
	entityPath?: string;
	navigate?: NavigateFunction;
}

const useStyles = makeStyles((theme) => ({
	dataGridWrapper: {
		'& .hidden': {
			fontStyle: 'italic',
		},
	},
	pageTitle: {
		fontSize: pageTitleSize,
		display: 'flex',
		alignItems: 'flex-end',
		marginRight: 'auto',
	},
	auxLink: {
		marginRight: theme.spacing(1),
		marginBottom: '5px',
	},
}));

const AuxDataView = (props: Props) => {
	const classes = useStyles();
	const {
		authData,
		errorData,
		entityLocator: entityLocatorFromProps,
		entityType,
		entityPath,
		backUrl,
		navigate,
	} = props;

	const params = useParams();
	const entityLocator = params[entityPath] || entityLocatorFromProps;
	const [auxDataKeysByPage, setAuxDataKeysByPage] = useState<
		AuxDataKeyResponse[][]
	>([]);
	const [loading, setLoading] = useState<boolean>(false);
	const [page, setPage] = useState(0);
	const [auxDataByPage, setAuxDataByPage] = useState<AuxDataResponse[][]>([]);
	const { authorizationToken } = authData.auth;
	const hasWritePermissions = authData.hasPermission('auxData.write');
	const hasDeletePermissions = authData.hasPermission('auxData.delete');
	const rowCount = auxDataKeysByPage.reduce(
		(sum, current) => sum + current.length,
		0,
	);

	const { t } = useTranslation();

	const auxDataEntityTypeNames: any = {
		quote: 'Quote',
		policy: 'Policy',
		policyholder: 'Policyholder',
		exposure: t('exposure.singular.upper'),
		peril: t('peril.singular.upper'),
		premiumReport: 'Premium Reports',
		endorsement: t('endorsement.singular.upper'),
		renewal: 'Renewal',
		cancellation: 'Cancellation',
		reinstatement: 'Reinstatement',
		claim: 'Claim',
		subclaim: 'Subclaim',
	};

	const loadDataAuxDataByPage = async () => {
		setLoading(true);
		try {
			let response = await AuxDataApi.getAuxDataKeys(
				authorizationToken,
				entityLocator,
				page,
			);
			auxDataKeysByPage[page] = response.data.keys;

			if (!auxDataByPage[page + 1]) {
				response = await AuxDataApi.getAuxDataKeys(
					authorizationToken,
					entityLocator,
					page + 1,
				);
				auxDataKeysByPage[page + 1] = response.data.keys;
			}

			const auxDataRequests = auxDataKeysByPage[page].map((auxDataKey) =>
				AuxDataApi.getAuxData(
					authorizationToken,
					entityLocator,
					auxDataKey.key,
				),
			);
			const responses = await axios.all(auxDataRequests);
			const responseData = responses.map(
				(auxDataResponse) => auxDataResponse.data,
			);
			auxDataByPage[page] = responseData;

			setAuxDataKeysByPage(auxDataKeysByPage);
			setAuxDataByPage(auxDataByPage);
		} catch (error) {
			errorData.handleErrorResponse(error, 'Failed to fetch aux data');
			if (await handleUnauthorizedResponse(error, authData)) {
				return;
			}
		}

		setLoading(false);
	};

	useEffect(() => {
		loadDataAuxDataByPage();
	}, [page]);

	const columns: GridColDef[] = [
		{ field: 'key', headerName: 'Key', flex: 0.4 },
		{ field: 'value', headerName: 'Value', flex: 0.4 },
	];

	if (hasWritePermissions || hasDeletePermissions) {
		const getRowActions = (params: GridRowParams) => {
			const rowActions = [];
			if (params.row.uiType === AuxDataUITypes.normal) {
				if (hasWritePermissions) {
					rowActions.push(
						<AuxDataEditModalTrigger
							key='edit'
							entityLocator={entityLocator}
							editKey={`${params.row.key}`}
							editValue={`${params.row.value}`}
							onConfirmCallback={loadDataAuxDataByPage}
						/>,
					);
				}

				if (hasDeletePermissions) {
					rowActions.push(
						<AuxDataDeleteModalTrigger
							key='delete'
							entityLocator={entityLocator}
							deleteKey={`${params.row.key}`}
							onConfirmCallback={loadDataAuxDataByPage}
						/>,
					);
				}
			}
			return rowActions;
		};

		columns.push({
			field: 'actions',
			type: 'actions',
			align: 'right',
			flex: 0.2,
			disableReorder: true,
			sortable: false,
			cellClassName: 'actions',
			getActions: getRowActions,
		});
	}

	const rows: GridRowsProp = (auxDataByPage[page] || [])
		.filter((auxData) => auxData.uiType !== AuxDataUITypes.hidden)
		.map((auxData, index) => {
			return {
				id: `${auxData.key}-${index}`,
				key: auxData.uiType === AuxDataUITypes.hidden ? 'Hidden' : auxData.key,
				value:
					auxData.uiType === AuxDataUITypes.hidden ? 'Hidden' : auxData.value,
				uiType: auxData.uiType,
			};
		});

	const getRowClassName = (params: GridRowParams) => {
		return `${params.row.uiType}`;
	};

	const EmptyTable = () => {
		return !auxDataByPage[page] || !auxDataByPage[page].length ? (
			<GridOverlay>No Aux Data</GridOverlay>
		) : null;
	};

	if (!entityLocator) {
		return <div>Entity Locator is required</div>;
	}
	return (
		<div>
			<StyledTitleContainer>
				<div
					className={classes.pageTitle}
				>{`Aux Data - ${auxDataEntityTypeNames[entityType]} ${entityLocator}`}</div>
				{!!backUrl && (
					<Button
						className={classes.auxLink}
						component={RouterLink}
						to={backUrl}
					>
						Back
					</Button>
				)}
				{!!navigate && (
					<Button className={classes.auxLink} onClick={() => navigate(-1)}>
						Back
					</Button>
				)}
				{hasWritePermissions && (
					<AuxDataCreateModalTrigger
						entityLocator={entityLocator}
						onConfirmCallback={loadDataAuxDataByPage}
					/>
				)}
			</StyledTitleContainer>
			<hr />
			<div style={{ height: '500px', width: '100%', marginTop: '10px' }}>
				<div style={{ display: 'flex', height: '100%' }}>
					<div className={classes.dataGridWrapper} style={{ flexGrow: 1 }}>
						<DataGrid
							paginationMode='server'
							pagination
							paginationModel={{ page, pageSize: 100 }}
							onPaginationModelChange={(e) => setPage(e.page)}
							rows={rows}
							columns={columns}
							loading={loading}
							rowCount={rowCount}
							getRowClassName={getRowClassName}
							disableRowSelectionOnClick
							disableColumnMenu
							slots={{
								noRowsOverlay: EmptyTable,
							}}
						/>
					</div>
				</div>
			</div>
		</div>
	);
};

function withContext(Component: any) {
	return (props: Omit<Props, 'authData' | 'configData' | 'errorData'>) => (
		<ErrorContext.Consumer>
			{(errorData) => (
				<AuthContext.Consumer>
					{(authData) => (
						<ConfigContext.Consumer>
							{(configData) => (
								<Component
									{...props}
									authData={authData}
									configData={configData}
									errorData={errorData}
								/>
							)}
						</ConfigContext.Consumer>
					)}
				</AuthContext.Consumer>
			)}
		</ErrorContext.Consumer>
	);
}

export default withContext(AuxDataView);
